import { useEffect } from "react";
import { allCommands, effectiveKeys, subscribeCommands } from "./registry";

/**
 * Global shortcut dispatch. We intentionally do NOT use a library here:
 * app shortcuts must fire even while the editor has focus (Ctrl+N in the
 * middle of typing), which keybinding libs refuse for editable targets.
 *
 * Safety rules:
 * - only modifier combos ($mod/Alt) ever fire globally; plain keys never do
 * - listens in the bubble phase and skips events the editor (or any other
 *   handler) already consumed via preventDefault — so Ctrl+B is bold inside
 *   the editor and "toggle sidebar" everywhere else
 */
export function useShortcuts() {
  useEffect(() => {
    let bindings = compile();
    const unsub = subscribeCommands(() => {
      bindings = compile();
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      for (const b of bindings) {
        if (matches(e, b)) {
          e.preventDefault();
          b.run();
          return;
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      unsub();
    };
  }, []);
}

interface Binding {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
  run: () => void;
}

function compile(): Binding[] {
  const out: Binding[] = [];
  for (const cmd of allCommands()) {
    const keys = effectiveKeys(cmd);
    if (!keys) continue;
    const parts = keys.split("+");
    const key = parts[parts.length - 1]!.toLowerCase();
    const b: Binding = { mod: false, shift: false, alt: false, key, run: cmd.run };
    for (const p of parts.slice(0, -1)) {
      const pl = p.toLowerCase();
      if (pl === "$mod") b.mod = true;
      else if (pl === "shift") b.shift = true;
      else if (pl === "alt") b.alt = true;
    }
    // global shortcuts must carry a real modifier
    if (b.mod || b.alt) out.push(b);
  }
  return out;
}

function matches(e: KeyboardEvent, b: Binding): boolean {
  const mod = e.ctrlKey || e.metaKey;
  if (b.mod !== mod || b.shift !== e.shiftKey || b.alt !== e.altKey) return false;
  return e.key.toLowerCase() === b.key;
}
